
/**
 * Session format standardization utilities
 * Standard formats: "in_person" and "virtual"
 */

export const STANDARD_SESSION_FORMATS = {
  IN_PERSON: 'in_person',
  VIRTUAL: 'virtual'
} as const;

export const SESSION_FORMAT_DISPLAY_NAMES = {
  [STANDARD_SESSION_FORMATS.IN_PERSON]: 'In Person',
  [STANDARD_SESSION_FORMATS.VIRTUAL]: 'Virtual'
} as const;

/**
 * Normalizes various session format representations to standard format
 */
export const normalizeSessionFormat = (format: string): string => {
  if (!format) return '';
  
  const normalized = format.toLowerCase().trim();
  
  // Handle various representations of in-person format
  if (normalized === 'in-person' || 
      normalized === 'in person' || 
      normalized === 'physical' ||
      normalized === 'in_person') {
    return STANDARD_SESSION_FORMATS.IN_PERSON;
  }
  
  // Handle various representations of virtual format
  if (normalized === 'virtual' || 
      normalized === 'online' ||
      normalized === 'remote') {
    return STANDARD_SESSION_FORMATS.VIRTUAL;
  }
  
  // Return original if no match (shouldn't happen with clean data)
  return format;
};

/**
 * Normalizes an array of session formats to standard formats
 */
export const normalizeSessionFormats = (formats: string[]): string[] => {
  if (!Array.isArray(formats)) return [];
  
  return formats
    .map(normalizeSessionFormat)
    .filter(format => format && Object.values(STANDARD_SESSION_FORMATS).includes(format as any));
};

/**
 * Gets the display name for a session format
 */
export const getSessionFormatDisplayName = (format: string): string => {
  const normalizedFormat = normalizeSessionFormat(format);
  return SESSION_FORMAT_DISPLAY_NAMES[normalizedFormat as keyof typeof SESSION_FORMAT_DISPLAY_NAMES] || format;
};

/**
 * Checks if a format array contains in-person sessions
 */
export const hasInPersonFormat = (formats: string[]): boolean => {
  return normalizeSessionFormats(formats).includes(STANDARD_SESSION_FORMATS.IN_PERSON);
};

/**
 * Checks if a format array contains virtual sessions
 */
export const hasVirtualFormat = (formats: string[]): boolean => {
  return normalizeSessionFormats(formats).includes(STANDARD_SESSION_FORMATS.VIRTUAL);
};
